/**
 * Per-variant Pokémon GO availability roster (1.8.7 — Task 4).
 *
 * The Profile "missing" glance and the trade surfaces previously treated the
 * full National Dex (1..1025) as the candidate pool, so species that are not
 * released in Pokémon GO — evaluated PER VARIANT — leaked in as "missing" tiles
 * and as trade recommendations. A species can be released as base/shiny while a
 * curated variant (Mega, G-Max, Shadow, Purified) is not available for it, so
 * availability must be decided per variant, not per species.
 *
 * Single source of truth both surfaces import so they can never drift:
 *   - curated variants (mega / gmax / shadow / purified): the same curated
 *     roster the Dex grid uses (`curatedRosterFor`).
 *   - base `pokemon` + `lucky` / `perfect` / `xxs` / `xxl`: the In-GO released
 *     roster. Those user-attribute variants have no distinct availability feed,
 *     so per the brief they fall back to the base In-GO roster rather than the
 *     full National list.
 *   - `shiny`: the shiny-released roster.
 *   - `shundo` / `shinyLucky`: manual-only variants; treated as shiny-available
 *     (a shundo/shiny-lucky can only exist where the shiny does).
 *
 * The In-GO / shiny rosters are bundled from PoGoAPI (inGoRoster.generated.ts)
 * as canonical National Dex ids, so filtering works offline and on-device
 * without waiting on the /api/dex-availability fetch.
 */
import { curatedReleasedRosterFor } from "./pogoAvailability";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";
import { RELEASED_IN_GO_IDS, SHINY_IN_GO_IDS } from "./generated/inGoRoster.generated";
import {
  applyRosterCorrections,
  RELEASED_ADD_IDS,
  RELEASED_REMOVE_IDS,
  SHINY_ADD_IDS,
  SHINY_REMOVE_IDS,
} from "./rosterCorrections";
import {
  GAME_MASTER_SPECIES_IDS,
  TRADABLE_IN_GO_IDS,
} from "./generated/tradableRoster.generated";
import { SHINY_FROM_EVENTS_IDS } from "./generated/eventReleases.generated";

// The bundled rosters come from PoGoAPI, which is structurally incomplete in
// BOTH directions — it misses released species and lists ones that aren't out.
// Apply the curated corrections BEFORE deriving any set, so every downstream
// roster (base, shiny, lucky, shiny-lucky) and every count sees one truth.
// See rosterCorrections.ts.
const RELEASED_IDS: readonly number[] = applyRosterCorrections(
  RELEASED_IN_GO_IDS,
  RELEASED_ADD_IDS,
  RELEASED_REMOVE_IDS,
);
/**
 * Shiny sources, in precedence order (lowest first):
 *   1. the bundled PoGoAPI floor (incomplete, frozen);
 *   2. SHINY_FROM_EVENTS_IDS — base-form shiny sightings the event watcher has
 *      ratcheted from LeekDuck (this is how a NEW shiny debut lands without
 *      anyone noticing it by hand);
 *   3. the curated corrections, which WIN — an in-game check by the owner beats
 *      both feeds, and `shiny: false` must be able to veto a bad auto-unlock.
 * applyRosterCorrections adds then removes, so (3) genuinely wins.
 */
const SHINY_IDS: readonly number[] = applyRosterCorrections(
  [...SHINY_IN_GO_IDS, ...SHINY_FROM_EVENTS_IDS],
  SHINY_ADD_IDS,
  SHINY_REMOVE_IDS,
);

const RELEASED_SET: ReadonlySet<number> = new Set(RELEASED_IDS);
const SHINY_SET: ReadonlySet<number> = new Set(SHINY_IDS);

/* ────────────────────────────────────────────────────────────────────────────
   Server-driven release overrides (admin dex-config editor).
   ----------------------------------------------------------------------------
   Force a species released / unreleased in GO from the portal without a rebuild
   — e.g. flip Cramorant #845 to released on its launch day, or SCHEDULE a shiny
   for a future date. The dex-config syncer feeds these from GET /api/dex-config
   into the shared releaseSchedule module, which evaluates each add's date at
   READ time (auto-flip). Applied on top of the bundled rosters, per variant:
     - 'pokemon' cascades to lucky/perfect/xxs/xxl (they derive from the base set)
     - 'shiny'   cascades to shundo/shinyLucky (they derive from the shiny set)
   Server-side a species is in at most one list per variant, so add and remove
   never conflict here.
   ──────────────────────────────────────────────────────────────────────────── */
import {
  activeAddSet,
  removeSet,
  hasFutureAdds,
  hasNoSchedule,
  releaseScheduleVersion,
  setReleaseSchedule,
  type ReleaseScheduleWire,
} from "./releaseSchedule";

/** Re-exported so the dex-config syncer imports both setters from one place. */
export { setReleaseSchedule };

/**
 * Back-compat shim for the old base-only setter (still used by tests). Replaces
 * the whole schedule with just the 'pokemon' axis, no dates — matching the
 * pre-0047 behaviour where only base release could be overridden.
 */
export function setRuntimeReleaseOverrides(
  released: readonly number[],
  unreleased: readonly number[],
): void {
  const wire: ReleaseScheduleWire = {
    pokemon: {
      released: released.map((dexId) => ({ dexId, releaseAt: null })),
      unreleased: [...unreleased],
    },
  };
  setReleaseSchedule(wire);
}

/**
 * Apply a variant's release-schedule overrides on top of a base roster. Returns
 * the base set by identity when the variant has no overrides (fast path). When a
 * variant carries a future-dated add, the result is NOT cacheable by the caller
 * (it must re-evaluate as time passes) — signalled by hasFutureAdds().
 */
function withSchedule(variant: PokedexVariant, base: ReadonlySet<number>): ReadonlySet<number> {
  if (hasNoSchedule(variant)) return base;
  const adds = activeAddSet(variant);
  const removes = removeSet(variant);
  if (adds.size === 0 && removes.size === 0) return base;
  const set = new Set<number>(base);
  removes.forEach((id) => set.delete(id));
  adds.forEach((id) => set.add(id));
  return set;
}

// Effective-set cache. Keyed on the schedule version; bypassed for a variant
// with a pending future-dated add so the read-time auto-flip stays exact.
const effectiveCache = new Map<string, { version: number; set: ReadonlySet<number> }>();
function cachedEffective(variant: PokedexVariant, base: ReadonlySet<number>): ReadonlySet<number> {
  if (hasNoSchedule(variant)) return base;
  if (hasFutureAdds(variant)) return withSchedule(variant, base); // read-time, uncached
  const version = releaseScheduleVersion();
  const hit = effectiveCache.get(variant);
  if (hit && hit.version === version) return hit.set;
  const set = withSchedule(variant, base);
  effectiveCache.set(variant, { version, set });
  return set;
}

/** The effective "released in GO" base set (bundled baseline ± base overrides). */
function effectiveReleasedSet(): ReadonlySet<number> {
  return cachedEffective("pokemon", RELEASED_SET);
}

/** The effective shiny-released set (bundled shiny baseline ± shiny overrides).
 *  This is the axis the Shiny completion ring reads, so an admin-added shiny
 *  (e.g. Solgaleo) now moves both the numerator and denominator. */
function effectiveShinySet(): ReadonlySet<number> {
  return cachedEffective("shiny", SHINY_SET);
}

/**
 * Lucky comes ONLY from a trade, so the Lucky rosters are the base/shiny
 * rosters intersected with what Pokémon GO says is tradable.
 *
 * Tradability is read from the Game Master (`pokemonSettings.isTradable`, see
 * tradableRoster.generated.ts). This replaced "the roster minus every
 * Mythical", which was a proxy for tradability and got it wrong both ways:
 * it let untradable Legendaries through (Zygarde #718, Eternatus #890) and
 * stripped the two tradable Mythicals (Meltan #808, Melmetal #809).
 *
 * The intersection matters: the Game Master lists tradability for unreleased
 * species too, so tradable-but-not-released must not become Lucky-available.
 */
const TRADABLE_SET: ReadonlySet<number> = new Set(TRADABLE_IN_GO_IDS);

/**
 * True iff Pokémon GO allows `dexId` to be traded at all — independent of
 * variant and of release state.
 *
 * Exported for the trade-lead surfaces (Compare's "You have, they don't" /
 * "They have, you don't"), where a species that cannot change hands is not a
 * lead on ANY axis, not just Lucky. The worker applies the same gate from its
 * byte-identical copy of the roster (cloudflare/src/lib/tradableRoster.ts);
 * this is the on-device half so an older deploy still can't show a dead lead.
 */
export function isTradableSpecies(dexId: number): boolean {
  return TRADABLE_SET.has(dexId);
}
/**
 * Baseline Lucky / Shiny-Lucky sets (no admin overrides) — used only for the
 * RELEASED_UNTRADABLE_COUNT constant below. The LIVE availability rosters are
 * `effectiveLuckySet()` / `effectiveShinyLuckySet()`, which derive from the
 * override-aware base/shiny sets so a portal release cascades to Lucky too.
 */
const LUCKY_BASELINE_SET: ReadonlySet<number> = new Set(
  RELEASED_IDS.filter((id) => TRADABLE_SET.has(id)),
);

/** Lucky availability = the effective (override-aware) released base ∩ tradable,
 *  then the variant's own direct 'lucky' overrides. So an admin adding a species
 *  to the base release also makes its Lucky available. */
function effectiveLuckySet(): ReadonlySet<number> {
  const base = effectiveReleasedSet();
  const luckyBase = base === RELEASED_SET
    ? LUCKY_BASELINE_SET
    : new Set([...base].filter((id) => TRADABLE_SET.has(id)));
  return withSchedule("lucky", luckyBase);
}

/** Shiny-Lucky availability = the effective shiny set ∩ tradable, then any direct
 *  'shinyLucky' overrides. Cascades from the shiny release. */
function effectiveShinyLuckySet(): ReadonlySet<number> {
  const shiny = effectiveShinySet();
  const shinyLuckyBase = shiny === SHINY_SET
    ? SHINY_LUCKY_BASELINE_SET
    : new Set([...shiny].filter((id) => TRADABLE_SET.has(id)));
  return withSchedule("shinyLucky", shinyLuckyBase);
}

const SHINY_LUCKY_BASELINE_SET: ReadonlySet<number> = new Set(
  SHINY_IDS.filter((id) => TRADABLE_SET.has(id)),
);

/**
 * Number of currently-released species excluded from the Lucky roster because
 * Pokémon GO says they cannot be traded. Baseline (no admin overrides).
 *
 * Renamed from RELEASED_MYTHICAL_COUNT: the exclusion is tradability, not
 * Mythical status. Those coincided under the old "minus Mythicals" rule, but no
 * longer — the set now includes untradable Legendaries (Zygarde #718,
 * Eternatus #890) and excludes the tradable Mythicals (Meltan #808,
 * Melmetal #809).
 */
export const RELEASED_UNTRADABLE_COUNT = RELEASED_SET.size - LUCKY_BASELINE_SET.size;

/* ────────────────────────────────────────────────────────────────────────────
   The trade gate, independent of release state.
   ----------------------------------------------------------------------------
   RELEASED_UNTRADABLE_COUNT above answers "how many RELEASED species can't be
   Lucky". The National ring asks a different question — "how many of the whole
   National Dex can never be Lucky" — and so does the write guard, which must
   reject an impossible catch whether or not the species is out yet.
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Every id the Game Master carries a pokemonSettings row for. This is the
 * COVERAGE set, and it is what separates the two kinds of absence from
 * TRADABLE_SET:
 *
 *   covered, not tradable  → Niantic has decided: UNTRADABLE. The flag is
 *                            omitted rather than written false, so silence
 *                            inside a row that exists is a real "no".
 *   not covered            → Niantic has said NOTHING. Unknown, never
 *                            untradable.
 *
 * Deriving "untradable" from "absent from TRADABLE_IN_GO_IDS and below the
 * highest tradable id" — what this used to do — conflated the two, and
 * Basculegion #902 was the id it got wrong (reported 2026-08-17): it is not in
 * Pokémon GO yet, has no row anywhere in the master, and was being reported as
 * a hard "can never be traded" purely because #890 and #893 sit either side of
 * it. Coverage also subsumes the old ceiling rule for free: a whole new
 * generation has no rows, so it is unknown and fails open exactly as before.
 */
const GAME_MASTER_COVERED_SET: ReadonlySet<number> = new Set(GAME_MASTER_SPECIES_IDS);

/** National ids the Game Master covers and does NOT mark tradable. */
const UNTRADABLE_NATIONAL_IDS: ReadonlySet<number> = (() => {
  const out = new Set<number>();
  for (const id of GAME_MASTER_COVERED_SET) {
    if (!TRADABLE_SET.has(id)) out.add(id);
  }
  return out;
})();

/**
 * True when Pokémon GO positively refuses to trade `dexId` — a hard rule of the
 * game, not a gap in our release data, and not a species the game has yet to
 * hear of. False for anything outside the Game Master's coverage.
 *
 * This is the honest replacement for `isMythicalDex` as a tradability test.
 * The two coincided once but no longer: Meltan #808, Melmetal #809 and
 * Pecharunt #1025 are tradable Mythicals, while Zygarde #718 and Eternatus #890
 * are untradable non-Mythicals.
 */
export function isKnownUntradableSpecies(dexId: number): boolean {
  return UNTRADABLE_NATIONAL_IDS.has(dexId);
}

/** How many ids in 1..nationalDexTotal can never be traded. */
export function untradableCountWithin(nationalDexTotal: number): number {
  let n = 0;
  for (const id of UNTRADABLE_NATIONAL_IDS) if (id <= nationalDexTotal) n += 1;
  return n;
}

/**
 * The dexes that can ONLY be obtained by trading, so an untradable species can
 * never appear in them on ANY axis — not merely "not released for them yet".
 */
const TRADE_GATED_VARIANTS: ReadonlySet<PokedexVariant> = new Set<PokedexVariant>([
  "lucky",
  "shinyLucky",
]);

/** True for the trade-only dexes (Lucky / Shiny-Lucky). */
export function isTradeGatedVariant(variant: PokedexVariant): boolean {
  return TRADE_GATED_VARIANTS.has(variant);
}

/**
 * True when `variant` can NEVER hold `dexId` by the rules of Pokémon GO itself.
 *
 * Deliberately narrower than `!isVariantAvailable(...)`, which also reports
 * "missing from our released roster". That roster is chronically incomplete in
 * both directions (rosterCorrections.ts exists for exactly that), and a manual
 * tap is the trainer's only way to correct it — so a release gap must never
 * block a hand-marked catch. A trade gate is different: no trade, no Lucky, ever.
 * This is the predicate the manual / trade write guards use.
 */
export function isImpossibleForVariant(variant: PokedexVariant, dexId: number): boolean {
  return isTradeGatedVariant(variant) && isKnownUntradableSpecies(dexId);
}

/**
 * The set of National Dex ids that can legitimately exist for `variant` in
 * Pokémon GO. Returns null only when we genuinely cannot know the roster, in
 * which case callers must NOT hide anything (fail open, never drop a real
 * missing entry). Every variant currently resolves to a concrete roster.
 */
export function availableRosterForVariant(variant: PokedexVariant): ReadonlySet<number> | null {
  // The curated dexes (mega/gmax/dynamax/shadow/purified) answer from their
  // roster, portal overrides and release dates applied. G-Max is the one
  // curated dex whose grid also lists forms GO has not shipped, so its
  // availability is the released subset (pogoAvailability.GMAX_RELEASED_IDS).
  const curated = curatedReleasedRosterFor(variant);
  if (curated) return new Set(curated);
  switch (variant) {
    case "shiny":
    // A shundo (shiny + perfect) is not trade-gated, so it keeps the full
    // shiny roster (override-aware).
    case "shundo":
      return effectiveShinySet();
    // Lucky is trade-only → effective base ∩ tradable (base overrides cascade).
    case "lucky":
      return effectiveLuckySet();
    // Shiny-Lucky is shiny AND trade-gated → effective shiny ∩ tradable.
    case "shinyLucky":
      return effectiveShinyLuckySet();
    // pokemon / perfect / xxs / xxl → base In-GO roster with admin release
    // overrides applied (a portal flip / scheduled date shows through app-wide).
    // perfect/xxs/xxl additionally honour their own direct overrides on top.
    default:
      return withSchedule(variant, effectiveReleasedSet());
  }
}

/**
 * True when `dexId` can exist for `variant` in Pokémon GO. Unknown rosters fail
 * open (return true) so a missing-roster case never hides a real entry.
 */
export function isVariantAvailable(variant: PokedexVariant, dexId: number): boolean {
  const roster = availableRosterForVariant(variant);
  if (!roster) return true;
  return roster.has(dexId);
}

/**
 * The dexes whose grid renders cells GO has not released, so a missing cell
 * can mean "not out yet" rather than "not caught yet" and gets flagged: the
 * full-National-Dex variants render EVERY species (1..1025), their shiny
 * siblings (Shiny Perfect, Shiny Lucky) do too, and the G-Max dex lists every
 * Gigantamax form that exists whether or not GO has shipped it. "locked" on
 * the shiny family (the shiny is gated behind release), "unreleased" on the
 * others. The other curated dexes (Mega/Dynamax/Shadow/Purified) only render
 * released members, so they never show an unavailable cell and are excluded.
 *
 * 2026-10-25: Shiny Perfect, Shiny Lucky and G-Max were missing here, so their
 * unreleased cells read as merely uncaught (no red ring, no label).
 */
const UNAVAILABLE_CALLOUT_VARIANTS: ReadonlySet<PokedexVariant> = new Set<PokedexVariant>([
  "pokemon",
  "shiny",
  "shundo",
  "shinyLucky",
  "lucky",
  "perfect",
  "xxs",
  "xxl",
  "gmax",
]);

export type UnavailableCallout = "locked" | "unreleased";

/**
 * The unavailable callout to show on a grid cell, or null when the species IS
 * available for the variant (or the variant doesn't get a callout). The shiny
 * family reads "locked"; the other dexes read "unreleased" (on G-Max it is the
 * Gigantamax form that GO has not released).
 */
export function unavailableCalloutFor(
  variant: PokedexVariant,
  dexId: number,
): UnavailableCallout | null {
  if (!UNAVAILABLE_CALLOUT_VARIANTS.has(variant)) return null;
  if (isVariantAvailable(variant, dexId)) return null;
  // "locked" = the species is in the game but cannot be obtained in THIS dex;
  // "unreleased" = it is not in the game yet. A shiny is always the former (the
  // species is out, its shiny is not), and Shiny Perfect / Shiny Lucky read the
  // same word as the Shiny dex they derive from. For Lucky the distinction is
  // tradability: a released-but-untradable species can never be Lucky, so it is
  // locked, while an unreleased one is simply not out.
  //
  // This used to key off isMythicalDex, which mislabelled both directions —
  // Zygarde #718 is a released untradable Legendary (locked, not unreleased),
  // and Meltan #808 is a tradable Mythical that is not locked at all.
  if (variant === "shiny" || variant === "shundo" || variant === "shinyLucky") return "locked";
  if (variant === "lucky" && RELEASED_SET.has(dexId) && !TRADABLE_SET.has(dexId)) return "locked";
  return "unreleased";
}
